import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {ConfigProvider, Modal, Segmented, Spin, notification} from "antd"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {EncheresAPI, identifierOf, formatMontant, hintIdentifie, poserHint} from "./api"
import {getLayoutDirection} from "../../locales"
import {ENCH_CSS} from "./styles"
import AuthScreen from "./AuthScreen"
import LotCard from "./LotCard"
import BidSheet from "./BidSheet"
import OffrirSheet from "./OffrirSheet"
import ProfilSheet from "./ProfilSheet"

// ─────────────────────────────────────────────────────────────────────
//  EncheresSpace — la page d'enchères du fidèle, partagée par les thèmes.
//
//  Même patron que l'espace compte : un composant agnostique du thème, habillé
//  par un petit wrapper dans chacun (header, footer, marge). Il pose lui-même
//  sa direction d'écriture et sa couleur d'accent.
//
//  Le direct, sans websocket : le produit n'en a pas, et une vente aux enchères
//  ne peut pas attendre un rechargement manuel. On interroge donc le serveur
//  toutes les trois secondes pendant qu'une vente est ouverte — mais de façon
//  SÉRIALISÉE (le battement suivant part quand le précédent est revenu), et en
//  annonçant la révision qu'on a déjà : le serveur répond « rien de neuf » sans
//  renvoyer la vente entière.
//
//  ⚠️ Contrairement au compteur de campagne, on ne COUPE PAS le rafraîchissement
//  après un temps d'inactivité : quelqu'un qui pose son téléphone pendant dix
//  minutes doit voir, en le reprenant, qu'il a été dépassé. On met seulement en
//  pause quand l'onglet est caché (le navigateur y ralentit les minuteurs de
//  toute façon), et on relit immédiatement au retour.
// ─────────────────────────────────────────────────────────────────────

const BATTEMENT_OUVERT = 3000
const BATTEMENT_FERME = 20000

export default function EncheresSpace({context}) {
    const org = context?.org || {}
    const identifier = identifierOf(org)
    const lang = context?.langValue || org?.language || "fr"
    const direction = getLayoutDirection(lang)
    // Couleur d'accent. Les thèmes officiels la posent déjà en CSS (`--primary`)
    // et la feuille de style la reprend telle quelle : cette valeur-ci ne sert
    // qu'aux quelques composants antd, qui ne lisent pas les variables CSS.
    // L'ordre suit celui des thèmes : jetons du 2026, puis couleur de l'org.
    const primary = context?.config?.theme2026?.tokens?.primary
        || org?.theme?.primary || org?.web?.theme?.primary
        || context?.config?.theme?.primary || "#2B70A9"

    // La vente demandée : celle de l'URL (page ENCHERES_VENTE), sinon la
    // première ouverte de l'organisation.
    const venteDemandee = context?.page?.venteId || (context?.route && context.route[1]) || null

    const [phase, setPhase] = useState("chargement")   // chargement | vide | vente
    const [instantane, setInstantane] = useState(null)
    const [identifie, setIdentifie] = useState(hintIdentifie())
    const [profil, setProfil] = useState(null)
    const [vue, setVue] = useState("lots")             // lots | moi
    const [lotEnCours, setLotEnCours] = useState(null)
    const [lotAOffrir, setLotAOffrir] = useState(null)
    const [reglagesOuverts, setReglagesOuverts] = useState(false)
    const [authPour, setAuthPour] = useState(null)     // lot en attente d'identification
    const [maintenant, setMaintenant] = useState(() => Date.now())

    const revRef = useRef(null)
    const visibleRef = useRef(true)

    // ── Chargement et battement ──────────────────────────────────────────
    const charger = useCallback(async () => {
        if (!identifier) return
        try {
            let venteId = venteDemandee
            if (!venteId) {
                const liste = await EncheresAPI.ventes(identifier)
                const ventes = (liste && liste.data) || []
                const ouverte = ventes.find(v => v.state === "OUVERTE") || ventes[0]
                if (!ouverte) {
                    setPhase("vide")
                    return
                }
                venteId = ouverte.id
            }
            const res = await EncheresAPI.vente(identifier, venteId)
            if (res && res.data) {
                setInstantane(res.data)
                revRef.current = res.data.rev
                setIdentifie(!!(res.data.moi && res.data.moi.contact))
                setPhase("vente")
            } else {
                setPhase("vide")
            }
        } catch (_) {
            setPhase("vide")
        }
    }, [identifier, venteDemandee])

    useEffect(() => { charger() }, [charger])

    const battre = useCallback(async () => {
        const vente = instantane && instantane.vente
        if (!identifier || !vente) return
        try {
            const res = await EncheresAPI.live(identifier, vente.id, revRef.current)
            if (!res || res.success === false) return
            if (res.inchange) return
            if (res.data) {
                const avant = instantane
                setInstantane(res.data)
                revRef.current = res.data.rev
                signalerDepassements(avant, res.data, lang)
            }
        } catch (_) { /* un battement raté n'est pas une erreur : le suivant réessaie */ }
    }, [identifier, instantane, lang])

    useEffect(() => {
        const vente = instantane && instantane.vente
        if (!vente) return undefined
        const ouverte = vente.state === "OUVERTE"
        const delai = ouverte ? BATTEMENT_OUVERT : BATTEMENT_FERME

        let annule = false
        let minuteur = null
        const tour = async () => {
            if (annule) return
            if (visibleRef.current) await battre()
            if (!annule) minuteur = setTimeout(tour, delai)
        }
        minuteur = setTimeout(tour, delai)

        const surVisibilite = () => {
            visibleRef.current = !document.hidden
            // Au retour sur l'onglet, on relit TOUT DE SUITE : c'est le moment
            // exact où le fidèle veut savoir s'il a été dépassé.
            if (!document.hidden) battre()
        }
        document.addEventListener("visibilitychange", surVisibilite)

        return () => {
            annule = true
            if (minuteur) clearTimeout(minuteur)
            document.removeEventListener("visibilitychange", surVisibilite)
        }
    }, [instantane && instantane.vente && instantane.vente.id, instantane && instantane.vente && instantane.vente.state, battre])

    // Le compte à rebours se cale sur l'heure du SERVEUR : l'horloge d'un
    // téléphone peut avoir plusieurs minutes d'écart, et afficher « 2 min »
    // quand il reste 10 secondes ferait rater une enchère.
    const decalage = useMemo(() => {
        if (!instantane || !instantane.serverTime) return 0
        return new Date(instantane.serverTime).getTime() - Date.now()
    }, [instantane && instantane.serverTime])

    useEffect(() => {
        const minuteur = setInterval(() => setMaintenant(Date.now()), 1000)
        return () => clearInterval(minuteur)
    }, [])

    // ── Actions ──────────────────────────────────────────────────────────
    const encherir = (lot) => {
        if (!identifie) {
            setAuthPour(lot)
            return
        }
        setLotEnCours(lot)
    }

    const acheter = async (lot) => {
        if (!identifie) {
            setAuthPour(lot)
            return
        }
        Modal.confirm({
            title: lot.kind === "FIXED" ? t`Prendre ce lot ?` : t`Emporter ce lot maintenant ?`,
            content: lot.kind === "FIXED"
                ? t`Vous vous engagez à donner ${formatMontant(lot.fixedPrice, instantane.vente.devise, lang)}.`
                : t`Vous vous engagez à donner ${formatMontant(lot.buyNowPrice, instantane.vente.devise, lang)}, et le lot est à vous immédiatement.`,
            okText: t`Je confirme`,
            cancelText: t`Annuler`,
            onOk: async () => {
                const res = await EncheresAPI.acheter(identifier, lot.id, {})
                if (!res || res.success === false) {
                    notification.warning({message: t`Ce lot n'est plus disponible.`})
                } else {
                    notification.success({message: t`C'est à vous.`})
                }
                await charger()
            },
        })
    }

    const apresIdentification = async () => {
        poserHint(true)
        setIdentifie(true)
        const lot = authPour && authPour.id ? authPour : null
        setAuthPour(null)
        await charger()
        if (lot) setLotEnCours(lot)
    }

    // ── Rendu ────────────────────────────────────────────────────────────
    if (phase === "chargement") {
        return <Habillage primary={primary} direction={direction}><div className="ench-vide"><Spin/></div></Habillage>
    }

    if (phase === "vide" || !instantane) {
        return (
            <Habillage primary={primary} direction={direction}>
                <div className="ench-vide">
                    <div className="ench-vide-titre"><Trans>Aucune vente en cours</Trans></div>
                    <div><Trans>Les prochaines enchères seront annoncées ici.</Trans></div>
                </div>
            </Habillage>
        )
    }

    const vente = instantane.vente
    const lots = instantane.lots || []
    const mesLots = lots.filter(l => l.moi)
    const parJour = grouperParJour(lots)
    const restant = vente.closesAt ? new Date(vente.closesAt).getTime() - (maintenant + decalage) : null
    const depasse = mesLots.filter(l => l.moi && !l.moi.meneur && l.state === "OUVERT" && !l.aveugle)

    return (
        <Habillage primary={primary} direction={direction}>
            <div className="ench-entete">
                {/* L'occasion se lit AVANT le titre, comme le sur-titre des pages
                    du thème : « CHABBAT VAYELECH · 5787 » puis le nom de la vente. */}
                {vente.occasion && vente.occasion.label && <div className="ench-occasion">{vente.occasion.label}</div>}
                <div className="ench-titre">{vente.label}</div>

                {vente.state === "OUVERTE" && restant !== null && (
                    <div className={"ench-compte" + (restant < 15 * 60000 ? " ench-compte-fin" : "")}>
                        {restant > 0
                            ? <Trans>Clôture dans {dureeLisible(restant)}</Trans>
                            : <Trans>Les enchères ferment…</Trans>}
                    </div>
                )}
                {vente.state !== "OUVERTE" && (
                    <div className="ench-compte"><Trans>Les enchères sont terminées.</Trans></div>
                )}
            </div>

            {/* Sans cette ligne, un gagnant qui ouvre l'e-mail depuis un autre
                appareil — ou dont le lot a été adjugé à l'oral par le gabbaï —
                n'a AUCUN moyen de se faire reconnaître : les boutons d'enchère,
                seule porte d'identification, disparaissent à la clôture. */}
            <div className="ench-identifier">
                {identifie ? (
                    /* La tribu vit ici : la feuille de mise refuse une montée
                       réservée « tant que vous n'avez pas dit si vous êtes Cohen,
                       Lévi ou Israël » — encore faut-il un endroit pour le dire. */
                    <button type="button" className="ench-lien" onClick={() => setReglagesOuverts(true)}>
                        <Trans>Mes réglages</Trans>
                    </button>
                ) : (
                    <button type="button" className="ench-lien" onClick={() => setAuthPour(true)}>
                        {vente.state === "OUVERTE"
                            ? <Trans>Vous avez déjà enchéri ? Identifiez-vous</Trans>
                            : <Trans>Vous avez remporté un lot ? Identifiez-vous</Trans>}
                    </button>
                )}
            </div>

            {depasse.length > 0 && (
                <div className="ench-bandeau ench-bandeau-alerte">
                    {depasse.length === 1
                        ? <Trans>Vous avez été dépassé sur {depasse[0].label}.</Trans>
                        : <Trans>Vous avez été dépassé sur {depasse.length} lots.</Trans>}
                </div>
            )}

            {identifie && mesLots.length > 0 && (
                <Segmented
                    block
                    value={vue}
                    onChange={setVue}
                    options={[
                        {value: "lots", label: t`Tous les lots`},
                        {value: "moi", label: t`Mes enchères (${mesLots.length})`},
                    ]}
                    style={{marginBottom: 16}}
                />
            )}

            {vue === "moi" ? (
                <div className="ench-mes">
                    {mesLots.map(lot => (
                        <div key={lot.id} className="ench-mes-ligne">
                            <div>
                                <div className="ench-mes-label">{lot.label}</div>
                                <div className="ench-mes-etat">
                                    {lot.moi.meneur
                                        ? <Trans>Vous menez à {formatMontant(lot.moi.montant, vente.devise, lang)}</Trans>
                                        : <Trans>Votre offre : {formatMontant(lot.moi.montant, vente.devise, lang)}</Trans>}
                                </div>
                            </div>
                            {lot.state === "OUVERT" && !lot.surPlace && (
                                <button type="button" className="ench-btn" style={{maxWidth: 140}} onClick={() => encherir(lot)}>
                                    {lot.moi.meneur ? <Trans>Augmenter</Trans> : <Trans>Reprendre la tête</Trans>}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                parJour.map(groupe => (
                    <div key={groupe.cle}>
                        {groupe.titre && <div className="ench-jour">{groupe.titre}</div>}
                        <div className="ench-lots">
                            {groupe.lots.map(lot => (
                                <LotCard key={lot.id} lot={lot} vente={vente} lang={lang} onEncherir={encherir} onAcheter={acheter} onOffrir={setLotAOffrir}/>
                            ))}
                        </div>
                    </div>
                ))
            )}

            <BidSheet
                ouvert={!!lotEnCours}
                lot={lotEnCours}
                vente={vente}
                lang={lang}
                identifier={identifier}
                onFerme={() => setLotEnCours(null)}
                onMise={async (res) => {
                    notification.success({
                        message: res.meneur ? t`Vous menez` : t`Votre enchère est enregistrée`,
                        description: res.prolonge ? t`La fin du lot a été repoussée de quelques minutes.` : undefined,
                    })
                    await battre()
                }}
            />

            <ProfilSheet
                ouvert={reglagesOuverts}
                identifier={identifier}
                onFerme={() => setReglagesOuverts(false)}
                onEnregistre={async () => {
                    notification.success({message: t`Vos réglages sont enregistrés`})
                    await charger()
                }}
                onDeconnecte={async () => {
                    setIdentifie(false)
                    setVue("lots")
                    await charger()
                }}
            />

            <OffrirSheet
                ouvert={!!lotAOffrir}
                lot={lotAOffrir}
                identifier={identifier}
                lang={lang}
                onFerme={() => setLotAOffrir(null)}
                onOffert={async () => {
                    notification.success({message: t`C'est noté`, description: t`Le gabbaï appellera ce nom.`})
                    await charger()
                }}
            />

            <Modal
                open={!!authPour}
                footer={null}
                onCancel={() => setAuthPour(null)}
                destroyOnClose
                title={null}
            >
                <AuthScreen
                    identifier={identifier}
                    org={org}
                    lang={lang}
                    onIdentifie={apresIdentification}
                    titre={authPour && authPour.id
                        ? t`Pour enchérir, identifiez-vous`
                        : t`Identifiez-vous`}
                />
            </Modal>
        </Habillage>
    )
}

// L'habillage commun : direction d'écriture, couleur d'accent, feuille de style.
function Habillage({children, primary, direction}) {
    return (
        <ConfigProvider direction={direction} theme={{token: {colorPrimary: primary}}}>
            <style dangerouslySetInnerHTML={{__html: ENCH_CSS}}/>
            <div className="ench" style={{"--ench-primary": primary}}>{children}</div>
        </ConfigProvider>
    )
}

// Les lots sont donnés dans l'ordre de l'office ; on les regroupe par jour quand
// la vente en couvre plusieurs (Roch Hachana, Souccot).
function grouperParJour(lots) {
    const groupes = []
    const index = new Map()
    for (const lot of lots) {
        const cle = lot.jour || "__sans"
        if (!index.has(cle)) {
            const groupe = {cle, titre: lot.jour ? formatJour(lot.jour) : null, lots: []}
            index.set(cle, groupe)
            groupes.push(groupe)
        }
        index.get(cle).lots.push(lot)
    }
    return groupes
}

// ⚠️ `new Date("2026-09-26")` est lu comme MINUIT UTC : affiché à New York ou
// Los Angeles, le jour recule d'un cran et l'en-tête annonce la veille. Un jour
// civil se construit donc à partir de ses composantes, dans le fuseau du
// navigateur — c'est une date de calendrier, pas un instant.
function formatJour(ymd) {
    try {
        const [a, m, j] = String(ymd).split("-").map(Number)
        if (!a || !m || !j) return ymd
        return new Intl.DateTimeFormat(undefined, {weekday: "long", day: "numeric", month: "long"})
            .format(new Date(a, m - 1, j))
    } catch (_) {
        return ymd
    }
}

function dureeLisible(ms) {
    const total = Math.max(0, Math.floor(ms / 1000))
    const jours = Math.floor(total / 86400)
    const heures = Math.floor((total % 86400) / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const secondes = total % 60
    if (jours > 0) return `${jours} j ${heures} h`
    if (heures > 0) return `${heures} h ${String(minutes).padStart(2, "0")}`
    return `${minutes}:${String(secondes).padStart(2, "0")}`
}

// Prévenir dans la page quand une mise vient de passer devant la nôtre. L'e-mail
// part de toute façon côté serveur ; ceci ne sert qu'à celui qui regarde.
function signalerDepassements(avant, apres, lang) {
    if (!avant || !avant.lots) return
    const precedent = new Map(avant.lots.map(l => [l.id, l]))
    for (const lot of apres.lots || []) {
        const vieux = precedent.get(lot.id)
        if (!vieux || !vieux.moi || !lot.moi) continue
        if (vieux.moi.meneur && !lot.moi.meneur) {
            notification.warning({
                message: t`Vous avez été dépassé`,
                description: `${lot.label} — ${formatMontant(lot.currentAmount, apres.vente.devise, lang)}`,
            })
        }
    }
}
